// ============================================================================
// ORGANISATION MASTER HOOKS
// ============================================================================
// Server-side paged list of one organisation kind for one tenant. Re-fetches
// when the tenant or the query changes; returns nothing until a tenant is chosen.

import { useState, useEffect, useCallback } from 'react';
import { organisationService } from 'api/services/organisationService';
import type { OrganisationKind, OrganisationSearchParams } from 'api/types/organisation.types';
import type { PaginationMeta } from './useTenantUsers';

const EMPTY_PAGINATION: PaginationMeta = {
  totalCount: 0, pageNumber: 1, pageSize: 25, totalPages: 0, hasNextPage: false, hasPreviousPage: false,
};

export const useOrganisationList = <T,>(kind: OrganisationKind, tenantId: string, params: OrganisationSearchParams) => {
  const [data, setData] = useState<T[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPage = useCallback(async () => {
    if (!tenantId) {
      setData([]);
      setPagination(EMPTY_PAGINATION);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await organisationService.list<T>(kind, tenantId, params);
      const paged = response.data;
      setData(paged?.items || []);
      setPagination(paged ? {
        totalCount: paged.totalCount,
        pageNumber: paged.pageNumber,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        hasNextPage: paged.hasNextPage,
        hasPreviousPage: paged.hasPreviousPage,
      } : EMPTY_PAGINATION);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [kind, tenantId, params]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  return { data, pagination, loading, error, refetch: fetchPage };
};
